<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'

import type { ImageFile, Reward, UserMerchant } from '@/types/types'
import type { TabKey } from '@/components/base/BaseTab.vue'
import { pathForZone, type AppearanceTab } from '../composables/webappPageTabs'

const props = withDefaults(
  defineProps<{
    baseUrl: string
    cguUrl?: string
    cguWording?: string
    checkinPoints?: number
    currentAppearanceTab: AppearanceTab
    currentTab: string
    hasFormCompletion?: boolean
    images: ImageFile[]
    logo: string
    merchant: UserMerchant
    missions?: unknown[]
    pointsLabel?: string
    primaryColor?: string
    privacyPolicyUrl?: string
    registrationTexts?: Record<string, string>
    rewards?: Reward[]
    slug?: string
    statusName?: string
    storeName: string
    subtitle?: string
    title: string
    usePoliteLanguage?: boolean
    hasPointsByCurrency?: boolean
    loyaltyPointsByCurrencyUnit?: string
  }>(),
  {
    images: () => [],
    primaryColor: '#000000'
  }
)

const iframeRef = ref<HTMLIFrameElement>()

const emit = defineEmits<{
  (e: 'update:currentAppearanceTab', value: AppearanceTab): void
}>()

const { t, locale } = useI18n()

const screenTabs = computed<TabKey[]>(() => [
  { value: 'home', name: t('pages.loyaltySupports.webapp.tabs.home') },
  { value: 'loyalty', name: t('pages.loyaltySupports.webapp.tabs.loyalty') }
])
const hasScreenTabs = computed(() => props.currentTab === 'appearance')

const dataUrlCache = new Map<string, string>()

const toDataUrl = async (path: string): Promise<string> => {
  const cached = dataUrlCache.get(path)
  if (cached) return cached

  try {
    const blob = await (await fetch(path)).blob()
    const dataUrl = await new Promise<string>((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.readAsDataURL(blob)
    })
    dataUrlCache.set(path, dataUrl)

    return dataUrl
  } catch (error) {
    console.error('Erreur lors de la conversion du blob:', error)

    return path
  }
}

const processImages = (images: ImageFile[]): Promise<string[]> =>
  Promise.all(images.map(({ path }) => (path.startsWith('blob:') ? toDataUrl(path) : Promise.resolve(path))))

const processedImages = shallowRef<string[]>([])
const isIframeReady = ref(false)

const previewPath = computed(() => pathForZone(props.currentAppearanceTab))

const sendPreviewData = () => {
  if (!isIframeReady.value || !iframeRef.value?.contentWindow) return

  const previewData = {
    type: 'PREVIEW_UPDATE',
    data: {
      cguUrl: props.cguUrl,
      cguWording: props.cguWording,
      checkinPoints: props.checkinPoints,
      hasFormCompletion: props.hasFormCompletion,
      hasPointsByCurrency: props.hasPointsByCurrency,
      images: [...processedImages.value],
      isLoggedIn: props.currentAppearanceTab === 'loyalty' || props.currentAppearanceTab === 'optin',
      locale: locale.value,
      logo: props.logo,
      loyaltyPointsByCurrencyUnit: props.loyaltyPointsByCurrencyUnit,
      merchant: JSON.stringify(props.merchant),
      missions: JSON.stringify(props.missions ?? []),
      openOptinSheet: props.currentAppearanceTab === 'optin',
      openPhoneSheet: props.currentAppearanceTab === 'phone',
      path: previewPath.value,
      pointsLabel: props.pointsLabel,
      primaryColor: props.primaryColor,
      privacyPolicyUrl: props.privacyPolicyUrl,
      registrationStep: props.currentAppearanceTab === 'profileInfo' ? 'customer-info' : 'store-selection',
      rewards: JSON.stringify(props.rewards),
      slug: props.slug,
      statusName: props.statusName,
      storeName: props.storeName,
      subtitle: props.subtitle,
      title: props.title,
      usePoliteLanguage: props.usePoliteLanguage,
      ...props.registrationTexts
    }
  }

  iframeRef.value.contentWindow.postMessage(previewData, props.baseUrl)
}

const debouncedSendPreviewData = useDebounceFn(sendPreviewData, 100)

watch(
  () => props.images,
  async (images) => {
    processedImages.value = await processImages(images)
    debouncedSendPreviewData()
  },
  { immediate: true }
)

watch(
  () => [
    props.primaryColor,
    props.title,
    props.subtitle,
    props.cguUrl,
    props.cguWording,
    props.privacyPolicyUrl,
    props.registrationTexts,
    props.currentAppearanceTab,
    props.hasFormCompletion,
    props.missions,
    props.statusName,
    props.usePoliteLanguage
  ],
  () => {
    debouncedSendPreviewData()
  }
)

const previewOrigin = computed(() => new URL(props.baseUrl, window.location.href).origin)

const handleMessage = (event: MessageEvent) => {
  if (event.origin !== previewOrigin.value) return

  if (event.data?.type === 'PREVIEW_READY') {
    isIframeReady.value = true
    sendPreviewData()
  }
}

onMounted(() => {
  window.addEventListener('message', handleMessage)
})

onUnmounted(() => {
  window.removeEventListener('message', handleMessage)
  dataUrlCache.clear()
})
</script>

<template>
  <div class="bg-white rounded-xl py-5 px-8 border border-border flex flex-col items-center gap-5">
    <BaseTab
      v-if="hasScreenTabs"
      :model-value="currentAppearanceTab"
      :tabs="screenTabs"
      disable-router
      @update:model-value="emit('update:currentAppearanceTab', $event as AppearanceTab)"
    />
    <!-- zoom (not transform) so the smaller phone also takes less room, while the webapp keeps its mobile width -->
    <div class="flex flex-col gap-5 [zoom:0.75] xl:[zoom:0.65] 2xl:[zoom:0.75]">
      <BasePhone size="lg">
        <iframe ref="iframeRef" :src="baseUrl" class="w-full h-full" title="Mobile Preview" />
      </BasePhone>
    </div>
  </div>
</template>
